import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a unix-seconds timestamp (number or numeric string) or ISO string to
 * a time-only string: `HH:MM:SS` in the user's local locale.
 * Use this for compact order list rows where only the time of execution matters.
 * For a full date+time display, use `formatIST()` instead.
 */
export function fmtOrderTime(raw?: string | number | null): string {
  if (!raw) return "—"
  let ms: number
  if (typeof raw === "number") {
    ms = raw < 1e10 ? raw * 1000 : raw
  } else {
    const num = Number(raw)
    if (!isNaN(num) && num > 0) {
      ms = num < 1e10 ? num * 1000 : num
    } else {
      ms = new Date(raw).getTime()
    }
  }
  const d = new Date(ms)
  if (isNaN(d.getTime())) return "—"
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
}

/**
 * Returns the human-readable display name for an instrument, falling back to
 * the raw tradingsymbol (`scrip_id`) when the backend hasn't populated a
 * `description` yet (e.g. rows imported before the display-name rollout).
 *
 * `description` is the friendly, Zerodha-style label (e.g. "NIFTY 16th JUN
 * 23600 CE") computed at import time — see backend utils/scripDisplayName.js.
 * Use it for the PRIMARY label in any trader-facing list; keep `scrip_id`
 * available as small secondary text.
 *
 * Accepts any object carrying a `scrip_id` and optional `description` — scrips,
 * orders, and positions all qualify.
 */
export function getDisplayName(
  x: { description?: string | null; scrip_id?: string | null } | null | undefined,
): string {
  if (!x) return ""
  return (x.description && x.description.trim()) || x.scrip_id || ""
}

// Returns the color for a P&L value
export function getPnlColor(value: number): string {
  if (value > 0) return "var(--color-profit)"
  if (value < 0) return "var(--color-sell)"
  return "var(--color-neutral)"
}

// Returns the buy/sell accent color
export function getBuySellColor(isBuy: boolean): string {
  return isBuy ? "var(--color-buy)" : "var(--color-sell)"
}

/**
 * Formats a timestamp to a full date+time string: `YYYY-MM-DD HH:mm:ss` in IST (Asia/Kolkata).
 * Accepts unix seconds (number), numeric strings, or ISO date strings.
 * Use this for detail views and admin panels where the full date context is needed.
 * For time-only display in order lists, use `fmtOrderTime()` instead.
 */
export function formatIST(timestamp: number | string | null | undefined): string {
  if (!timestamp) return ""

  let date: Date
  // Handle Unix numeric timestamps (seconds)
  if (
    typeof timestamp === "number" ||
    (typeof timestamp === "string" && /^\d+$/.test(timestamp.trim()))
  ) {
    date = new Date(Number(timestamp) * 1000)
  } else {
    date = new Date(timestamp)
  }

  if (isNaN(date.getTime())) return String(timestamp)

  // Since the project now runs in IST from the server side,
  // we use Intl to consistently display Asia/Kolkata.
  // Format: YYYY-MM-DD HH:mm:ss
  const parts = new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date)

  const p: Record<string, string> = {}
  parts.forEach((part) => {
    p[part.type] = part.value
  })

  const hh = p.hour === "24" ? "00" : p.hour
  return `${p.year}-${p.month}-${p.day} ${hh}:${p.minute}:${p.second}`
}

/**
 * Returns today's date as `YYYY-MM-DD` in IST (Asia/Kolkata) — file-safe (sortable,
 * no separators that conflict with download filenames).
 *
 * Use this when stamping a filename or any user-facing artifact that must
 * align with the platform's IST-everywhere convention. The naive
 * `new Date().toISOString().split("T")[0]` returns a UTC date, which diverges
 * from IST between 18:30 IST and 05:30 IST — producing yesterday's date in
 * filenames stamped during late-evening admin reviews.
 *
 * Mirrors the backend helper at backend/src/utils/exportFilename.js so
 * frontend `link.download` filenames stay consistent with the backend's
 * Content-Disposition.
 */
export function todayIST(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(
    new Date(),
  )
}

/**
 * Format an instrument-expiry date string ("YYYY-MM-DD" from Zerodha's Kite
 * Connect instruments CSV — see backend importScrips.service.js) as a
 * human-readable date — regardless of the viewer's browser timezone.
 *
 * Why this exists:
 *   `new Date("2026-05-29")` parses as UTC midnight per the ECMAScript spec.
 *   Calling `.toLocaleDateString()` then formats in the *viewer's* local TZ.
 *   For anyone west of UTC (e.g. NRI traders on PST/HST, admins behind a
 *   non-Indian VPN), that subtracts hours and renders the **previous day**.
 *   Splitting the ISO string into Y/M/D components and constructing the
 *   Date with the local-date constructor avoids the UTC interpretation —
 *   the displayed date matches the contract's published expiry day in any
 *   TZ.
 *
 * `iso` is expected as exactly `YYYY-MM-DD` (10 chars). Anything that
 * fails the split returns the fallback ("—" by default).
 */
export function formatExpiryDate(
  iso: string | null | undefined,
  options: { fallback?: string; locale?: string; includeYear?: boolean } = {},
): string {
  const { fallback = "—", locale = "en-IN", includeYear = true } = options
  if (!iso || typeof iso !== "string") return fallback
  const parts = iso.split("-")
  if (parts.length !== 3) return fallback
  const y = Number(parts[0])
  const m = Number(parts[1])
  const d = Number(parts[2])
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return fallback
  const dt = new Date(y, m - 1, d)
  if (isNaN(dt.getTime())) return fallback
  return dt.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    ...(includeYear ? { year: "numeric" } : {}),
  })
}

/**
 * Formats a number using the Indian numbering system (e.g., 1,20,000.00).
 * Supports abbreviation (K, L, Cr) based on NEXT_PUBLIC_ENABLE_ABBREVIATION enviroment variable.
 */
const ENABLE_ABBREVIATION = process.env.NEXT_PUBLIC_ENABLE_ABBREVIATION === "true"

/**
 * Single source of truth for the number of decimal places shown in all
 * currency/price displays across the app. Change this one constant to
 * instantly update every formatCurrency() call that relies on the default.
 *
 * Exceptions that always use a fixed decimal count:
 *   - Integer quantities: pass `decimals: 0` explicitly
 *   - Percentages: pass `decimals: 2` explicitly
 */
export const DISPLAY_DECIMALS = 2

// Cache for Intl.NumberFormat instances to avoid re-creation overhead in render cycles
const formatterCache = new Map<string, Intl.NumberFormat>()

function getFormatter(decimals: number, noComma: boolean): Intl.NumberFormat {
  const key = `${decimals}-${noComma}`
  let formatter = formatterCache.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: !noComma,
    })
    formatterCache.set(key, formatter)
  }
  return formatter
}

export type FormatCurrencyOptions = {
  abbreviate?: boolean
  decimals?: number
  includeSymbol?: boolean
  noComma?: boolean
}

export function formatCurrency(
  value: number | string | undefined | null,
  options: FormatCurrencyOptions = {}
): string {
  if (value === undefined || value === null) return "0.00"

  const num = typeof value === "string" ? Number(value) : value
  if (!Number.isFinite(num)) return "0.00"

  const {
    abbreviate = ENABLE_ABBREVIATION,
    decimals = DISPLAY_DECIMALS,
    includeSymbol = false,
    noComma = false,
  } = options

  const abs = Math.abs(num)
  const sign = num < 0 ? "-" : ""
  const symbol = includeSymbol ? "₹" : ""

  if (abbreviate) {
    if (abs >= 1e7) {
      const v = abs / 1e7
      return `${sign}${symbol}${v.toFixed(decimals)}Cr`
    }

    if (abs >= 1e5) {
      const v = abs / 1e5
      return `${sign}${symbol}${v.toFixed(decimals)}L`
    }

    if (abs >= 1e3) {
      const v = abs / 1e3
      return `${sign}${symbol}${v.toFixed(decimals)}K`
    }
  }

  // Use cached Intl.NumberFormat for high performance inside render cycles
  const formatted = getFormatter(decimals, noComma).format(abs)
  return `${sign}${symbol}${formatted}`
}

/**
 * Render the mode-aware "Lots/Units" value as `count/units`.
 *
 * The backend computes the { count, units } pair (see backend/src/utils/lotsUnits.js):
 *   - Lot mode:      e.g. 5 / 5500
 *   - Quantity mode: e.g. 0.033 / 1
 *
 * Integer parts use Indian-comma grouping (consistent with the rest of the
 * tables); a fractional count is shown to 3dp with trailing zeros trimmed.
 * If either input is missing/non-finite, falls back to the bare count so the
 * cell never renders "NaN/…".
 *
 * When `single` is set (NSE Equity/Stocks, which have no lot concept), only the
 * unit count is rendered (e.g. "2"), with no slash.
 */
export function formatLotsUnits(
  count: number | string | undefined | null,
  units: number | string | undefined | null,
  single = false
): string {
  const c = typeof count === "string" ? Number(count) : count
  const u = typeof units === "string" ? Number(units) : units
  if (single) {
    return u !== undefined && u !== null && Number.isFinite(u) ? fmtLotsPart(u as number) : ""
  }
  if (c === undefined || c === null || !Number.isFinite(c)) return ""
  if (u === undefined || u === null || !Number.isFinite(u)) {
    return fmtLotsPart(c as number)
  }
  return `${fmtLotsPart(c as number)}/${fmtLotsPart(u as number)}`
}

function fmtLotsPart(n: number): string {
  if (Number.isInteger(n)) {
    return formatCurrency(n, { abbreviate: false, decimals: 0 })
  }
  // Default: 3dp, trim trailing zeros / dangling decimal point.
  let s = n.toFixed(3).replace(/\.?0+$/, "")
  if (parseFloat(s) === 0) {
    // A non-zero value rounded away at 3dp (e.g. COPPER lot size 2500, qty 1 →
    // 0.0004). Expand precision to show the first significant digit, not "0".
    const decimals = Math.min(8, Math.floor(-Math.log10(Math.abs(n))) + 1)
    s = n.toFixed(decimals).replace(/\.?0+$/, "")
  }
  return s
}

/**
 * Returns display attributes for a P&L value.
 * Zero values are neutral (no sign, inherits parent text color) so we never
 * render "+0.00" as profit-green or "-0.00" as loss-red across the trader app.
 *
 * - `color`     — CSS color string for inline `style={{ color }}`. `undefined` for zero (inherits parent).
 * - `className` — Tailwind text color class (`text-profit` / `text-sell` / `text-ink`).
 * - `sign`      — Use when rendering the value via formatCurrency directly (negative already has "-").
 * - `signAbs`   — Use when rendering with `Math.abs(value)` (caller provides the explicit "-" for negatives).
 */
export function getPnlDisplay(value: number | string | undefined | null): {
  color: string | undefined
  className: string
  sign: string
  signAbs: string
} {
  const num = typeof value === "string" ? Number(value) : (value ?? 0)
  if (!Number.isFinite(num) || num === 0) {
    return { color: undefined, className: "text-ink", sign: "", signAbs: "" }
  }
  if (num > 0) {
    return { color: "var(--color-profit)", className: "text-profit", sign: "+", signAbs: "+" }
  }
  return { color: "var(--color-sell)", className: "text-sell", sign: "", signAbs: "-" }
}
